// D:\directiveai-react\frontend\src\components\home\HomeInput\layouts\MinimalLayout.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery, useTheme, Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import {
  ContentWrapper,
  TypedText,
  StyledForm,
  SectionsContainer,
  Section,
  CardsRow,
  WaitingMessage,
} from '../HomeInputStyles';

// Componentes reutilizables
import InputField from '../components/InputField';
import PromptCard from '../components/PromptCard';
import CustomizeButton from '../components/CustomizeButton';

// Variantes de animación tipo macOS
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// const sectionVariants = { // Unused variable
//   hidden: {
//     opacity: 0,
//     y: 20
//   },
//   visible: {
//     opacity: 1,
//     y: 0,
//     transition: {
//       duration: 0.5,
//       ease: [0.25, 0.46, 0.45, 0.94]
//     }
//   }
// };

const MinimalLayout = ({
  state,
  isWaiting,
  inputRef,
  handleMessageSubmit,
  updateState,
  debouncedUpdateSuggestions,
  waitingMessage,
  AGT_CARDS,
  randomPromptCards,
  handleCardClick,
  isPro,
  selectedCard,
  handleCustomizeClick,
  interfaceStyle,
  t
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeSection, setActiveSection] = React.useState('explore');
  
  // Si no recibimos t como prop, usamos el hook
  const translation = useTranslation();
  const { t: tFunc } = translation;
  const translate = t || tFunc;

  // Efecto para alternar entre secciones en móvil
  React.useEffect(() => {
    if (isMobile && !isWaiting) {
      const interval = setInterval(() => {
        setActiveSection(prev => prev === 'explore' ? 'ask' : 'explore');
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isMobile, isWaiting]);

  // Renderizado condicional de secciones para móvil
  const renderMobileSections = () => {
    return (
      <Box sx={{ 
        position: 'relative', 
        height: '210px', 
        width: '100%', 
        maxWidth: '800px',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        margin: '20px auto 0'
      }}>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            maxWidth: '800px',
            opacity: activeSection === 'explore' ? 1 : 0,
            transition: 'opacity 0.8s cubic-bezier(0.42, 0, 0.58, 1)',
            pointerEvents: activeSection === 'explore' ? 'auto' : 'none',
            margin: '0 auto',
            left: 0,
            right: 0
          }}
        >
          <SectionComponent
            title="explore"
            cards={AGT_CARDS}
            onCardClick={handleCardClick}
            isPro={isPro}
            selectedCard={selectedCard}
            interfaceStyle={interfaceStyle}
            translate={translate}
          />
          <Box display="flex" justifyContent="center" width="100%">
            <CustomizeButton onClick={handleCustomizeClick} />
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            maxWidth: '800px',
            opacity: activeSection === 'ask' ? 1 : 0,
            transition: 'opacity 0.8s cubic-bezier(0.42, 0, 0.58, 1)',
            pointerEvents: activeSection === 'ask' ? 'auto' : 'none',
            margin: '0 auto',
            left: 0,
            right: 0
          }}
        >
          <SectionComponent
            title="ask"
            cards={randomPromptCards}
            onCardClick={handleCardClick}
            isPro={isPro}
            selectedCard={selectedCard}
            interfaceStyle={interfaceStyle}
            translate={translate}
          />
          <Box display="flex" justifyContent="center" width="100%">
            <CustomizeButton onClick={handleCustomizeClick} />
          </Box>
        </Box>
      </Box>
    );
  };

  // Renderizado normal para desktop
  const renderDesktopSections = () => {
    return (
      <>
        <SectionsContainer sx={{ 
          justifyContent: 'center', 
          margin: '20px auto 0',
          maxWidth: '800px',
          width: '100%'
        }}>
          <SectionComponent
            title="explore"
            cards={AGT_CARDS}
            onCardClick={handleCardClick}
            isPro={isPro}
            selectedCard={selectedCard}
            interfaceStyle={interfaceStyle}
            translate={translate}
          />
          <SectionComponent
            title="ask"
            cards={randomPromptCards}
            onCardClick={handleCardClick}
            isPro={isPro}
            selectedCard={selectedCard}
            interfaceStyle={interfaceStyle}
            translate={translate}
          />
        </SectionsContainer>
        <Box width="100%" maxWidth="800px" px={0} mt={2} display="flex" justifyContent="center">
          <CustomizeButton onClick={handleCustomizeClick} />
        </Box>
      </>
    );
  };

  return (
    <ContentWrapper
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        width: '100%',
        maxWidth: '1200px',
        position: 'relative',
        minHeight: '100vh',
      }}
    >
      <TypedText
        component={motion.h2}
        variants={itemVariants}
        variant="h2"
        gutterBottom
        sx={{
          textAlign: 'center !important',
          width: '100%',
          margin: '0 auto',
          marginBottom: '15px'
        }}
      >
        {translate('homeInput.heading')}
      </TypedText>

      {/* Input field positioned directly below the title */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}
      >
        <StyledForm onSubmit={handleMessageSubmit}>
          <InputField
            state={state}
            isWaiting={isWaiting}
            inputRef={inputRef}
            onSubmit={handleMessageSubmit}
            onStateUpdate={updateState}
            onSuggestionsUpdate={debouncedUpdateSuggestions}
            interfaceStyle={interfaceStyle}
            onSuggestionClick={handleCardClick}
          />
        </StyledForm>
      </Box>

      <AnimatePresence mode="wait">
        {isWaiting && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <WaitingMessage>
              {waitingMessage}...
            </WaitingMessage>
          </motion.div>
        )}
      </AnimatePresence>

      {!isWaiting && (
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {isMobile ? renderMobileSections() : renderDesktopSections()}
        </Box>
      )}
    </ContentWrapper>
  );
};

// Componente SectionComponent extraído para reutilización
const SectionComponent = ({ title, cards, onCardClick, isPro, selectedCard, interfaceStyle, translate }) => {
  const theme = useTheme();

  return (
    <Section>
      <Typography variant="h5" sx={{
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        width: '100%',
        textAlign: 'left',
        fontSize: '21px',
        lineHeight: 1.19048,
        letterSpacing: '0.011em',
        color: theme.palette.actualMode === 'wallpaper'
          ? '#FFFFFF'
          : 'inherit',
        [theme.breakpoints.down('sm')]: {
          marginBottom: theme.spacing(0.5),
          fontSize: '21px',
        }
      }}>
        {translate(`homeInput.sections.${title}`)}
      </Typography>
      <CardRow 
        cardData={cards}
        onCardClick={onCardClick}
        isPro={isPro}
        selectedCard={selectedCard}
        interfaceStyle={interfaceStyle}
      />
    </Section>
  );
};

// Componente CardRow extraído para reutilización
const CardRow = ({ cardData, onCardClick, isPro, selectedCard, interfaceStyle }) => (
  <CardsRow>
    {cardData.map((card, index) => (
      <React.Fragment key={`${card.text}-${index}`}>
        <PromptCard
          Icon={card.Icon}
          text={card.text}
          action={card.action}
          isProOnly={card.isProOnly}
          isPro={isPro}
          onClick={() => onCardClick(card.text)}
          selected={selectedCard === card.text}
          interfaceStyle={interfaceStyle}
        />
      </React.Fragment>
    ))}
  </CardsRow>
);

MinimalLayout.propTypes = {
  state: PropTypes.object.isRequired,
  isWaiting: PropTypes.bool.isRequired,
  inputRef: PropTypes.object.isRequired,
  handleMessageSubmit: PropTypes.func.isRequired,
  updateState: PropTypes.func.isRequired,
  debouncedUpdateSuggestions: PropTypes.func.isRequired,
  waitingMessage: PropTypes.string.isRequired,
  AGT_CARDS: PropTypes.array.isRequired,
  randomPromptCards: PropTypes.array.isRequired,
  handleCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  handleCustomizeClick: PropTypes.func.isRequired,
  interfaceStyle: PropTypes.string.isRequired,
  t: PropTypes.func,
  onSuggestionClick: PropTypes.func // ✅ AÑADIR ESTA LÍNEA SI NO ESTÁ
};

SectionComponent.propTypes = {
  title: PropTypes.string.isRequired,
  cards: PropTypes.array.isRequired,
  onCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  interfaceStyle: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired
};

CardRow.propTypes = {
  cardData: PropTypes.array.isRequired,
  onCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  interfaceStyle: PropTypes.string.isRequired
};

export default MinimalLayout;